import * as Sentry from '@sentry/vue';
import type { App } from 'vue';
import type { Router } from 'vue-router';
import type { IUser } from './types';

/**
 * Initialises Sentry for the Vue application.
 *
 * Call this function once in main.ts, BEFORE app.mount().
 * Pass the Vue `app` instance and the `router` so that
 * - BrowserTracing can instrument navigation performance
 * - Replay can attach to the Vue component tree
 * - Route params (id, friendlyCode, token) are tagged on every event
 *   for easy filtering and replay lookup in the Sentry dashboard.
 */
export function initSentry(app: App, router: Router): void {
  if (!import.meta.env.VITE_SENTRY_DSN) return;

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Capture 100 % of traces in dev; lower this in production via env if needed.
    tracesSampleRate: 1.0,
    // Record 10 % of all sessions and 100 % of sessions that contain an error.
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.VITE_APP_MODE || 'production',
  });

  // ── Route-based context ───────────────────────────────────────────────────
  // Tags every subsequent Sentry event with the active route so that replays
  // and error logs can be filtered by route name or the entity ID in the URL.
  router.afterEach((to) => {
    const scope = Sentry.getCurrentScope();
    scope.setTag('route.name', String(to.name ?? ''));
    scope.setTag('route.path', to.path);

    // Capture the primary entity identifier from the URL params so that
    // replays tied to a specific reservation / form / token are easy to find.
    const entityId =
      to.params.id ??
      to.params.friendlyCode ??
      to.params.token ??
      to.params.source;
    if (entityId) {
      scope.setTag('route.id', String(entityId));
    } else {
      scope.setTag('route.id', '');
    }
  });

  // ── console.error forwarding ──────────────────────────────────────────────
  // Any console.error() call automatically creates a Sentry event tagged with
  // the calling file, function name and line so errors are searchable without
  // manual instrumentation.
  const originalConsoleError = console.error.bind(console);

  console.error = (...args: unknown[]): void => {
    originalConsoleError(...args);

    const caller = extractCaller();
    const firstArg = args[0];
    const error =
      firstArg instanceof Error
        ? firstArg
        : new Error(args.map((a) => (typeof a === 'string' ? a : JSON.stringify(a))).join(' '));

    Sentry.withScope((scope) => {
      scope.setTag('source', 'console.error');
      // Structured caller context ─ visible in Sentry's tag search
      if (caller.file)     scope.setTag('caller.file', caller.file);
      if (caller.fn)       scope.setTag('caller.fn', caller.fn);
      if (caller.location) scope.setTag('caller.location', caller.location);
      if (caller.feature)  scope.setTag('feature', caller.feature);
      if (args.length > 1) scope.setExtra('args', args.slice(1));
      Sentry.captureException(error);
    });
  };
}

// ── Stack-trace caller extraction ────────────────────────────────────────────
// Parses a synthetic Error stack to find the first frame that belongs to the
// application (not to sentry.ts itself or browser internals), then returns
// structured tags that appear in Sentry's search / filter UI.

interface CallerInfo {
  /** Bare file name, e.g. "auth.store.ts" */
  file: string;
  /** Function / method name, e.g. "login" */
  fn: string;
  /** "file:line:col", e.g. "auth.store.ts:45:12" */
  location: string;
  /**
   * Dotted feature path mirroring the project structure:
   * "stores/auth.store → login"
   * Used as the primary Sentry tag for grouping / filtering.
   */
  feature: string;
}

function extractCaller(): Partial<CallerInfo> {
  const stack = new Error().stack ?? '';
  const lines = stack.split('\n');

  // Chrome/Edge/Node:  "    at fnName (file.ts:line:col)"
  // Firefox/Safari:    "fnName@file.ts:line:col"
  const chromeRe = /^\s+at (?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?$/;
  const firefoxRe = /^(?:(.+?)@)?(.+?):(\d+):(\d+)$/;

  for (const line of lines) {
    const m = chromeRe.exec(line) ?? firefoxRe.exec(line);
    if (!m) continue;

    const [, rawFn = '<anonymous>', rawFile = ''] = m;

    // Skip frames from this helper file and native/Sentry internals.
    if (
      rawFile.includes('sentry.ts') ||
      rawFile.includes('sentry.js') ||
      rawFile.startsWith('node:') ||
      rawFile.includes('<anonymous>')
    ) continue;

    // Strip Vite's "/@fs/" or absolute paths down to "src/…"
    const srcIndex = rawFile.indexOf('/src/');
    const relativePath = srcIndex !== -1 ? rawFile.slice(srcIndex + 1) : rawFile;

    // Bare file name for the tag value
    const fileParts = relativePath.split('/');
    const file = fileParts[fileParts.length - 1].split('?')[0]; // strip query params

    // Clean up the function name (remove "bound ", class prefixes, etc.)
    const fn = rawFn.replace(/^bound\s+/, '').split('.').pop() ?? rawFn;

    // Feature = "stores/auth.store → login" (folder/file → function)
    const featurePath = fileParts.slice(0, -1).join('/') + '/' + file.replace(/\.[^.]+$/, '');
    const feature = fn && fn !== '<anonymous>' ? `${featurePath} → ${fn}` : featurePath;

    const lineNo = m[3];
    const colNo  = m[4];

    return {
      file,
      fn: fn || '<anonymous>',
      location: `${file}:${lineNo}:${colNo}`,
      feature,
    };
  }

  return {};
}

// ── User identity ─────────────────────────────────────────────────────────────
// Call setSentryUser after a successful login and setSentryUser(null) on logout
// so that replays and errors are attributed to the correct account.

type SentryUserPayload = Pick<IUser, 'id' | 'email' | 'name' | 'role'>;

export function setSentryUser(user: SentryUserPayload | null | undefined): void {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.name,
      // `segment` is a free-form string; using role here lets you filter
      // replays / errors by user type in the Sentry dashboard.
      segment: String(user.role),
    });
  } else {
    Sentry.setUser(null);
  }
}

// ── Explicit error capture with context ───────────────────────────────────────

export interface SentryContext {
  /**
   * Identifies the origin of the error.
   * Use a dotted path that mirrors the file/function hierarchy, e.g.:
   *   "auth.store/login"
   *   "fetch-wrapper/request"
   *   "residence.store/saveResidence"
   */
  feature: string;
  /** Any additional key-value pairs useful for debugging. */
  extra?: Record<string, unknown>;
}

/**
 * Captures an exception in Sentry with structured context.
 *
 * The `feature` field identifies the origin (file → function). When omitted
 * it is inferred automatically from the call-site stack trace.
 *
 * @example
 * captureError(err, { feature: 'auth.store/login', extra: { email } });
 * captureError(err, { extra: { residenceId } }); // feature auto-detected
 */
export function captureError(error: unknown, context: Partial<SentryContext> = {}): void {
  const caller = extractCaller();
  Sentry.withScope((scope) => {
    const feature = context.feature ?? caller.feature ?? '';
    if (feature)         scope.setTag('feature', feature);
    if (caller.file)     scope.setTag('caller.file', caller.file);
    if (caller.fn)       scope.setTag('caller.fn', caller.fn);
    if (caller.location) scope.setTag('caller.location', caller.location);
    if (context.extra)   scope.setExtras(context.extra);
    const err = error instanceof Error ? error : new Error(String(error));
    Sentry.captureException(err);
  });
}

/**
 * Captures a diagnostic message in Sentry with structured context.
 *
 * @example
 * captureMessage('Subscription expired', 'warning', { feature: 'auth.store/handlePostLogin' });
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info',
  context?: Partial<SentryContext>,
): void {
  const caller = extractCaller();
  Sentry.withScope((scope) => {
    const feature = context?.feature ?? caller.feature ?? '';
    if (feature)         scope.setTag('feature', feature);
    if (caller.file)     scope.setTag('caller.file', caller.file);
    if (caller.fn)       scope.setTag('caller.fn', caller.fn);
    if (caller.location) scope.setTag('caller.location', caller.location);
    if (context?.extra)  scope.setExtras(context.extra);
    Sentry.captureMessage(message, level);
  });
}
